import type { PayrollLineItem } from '@/api/types';

/**
 * A payroll row's state, derived from its numbers.
 *
 * - blocked: owed money but can't be paid — missing payment details, or the
 *   payout route couldn't be resolved (providerKey 'unknown'). Someone owed
 *   $0 is never "blocked": there's nothing to unblock.
 * - in_flight: a payout is already queued or sent; a new one would be refused.
 * - ready: owed money and payable now.
 * - idle: nothing owed.
 */
export type PayrollRowState = 'ready' | 'blocked' | 'in_flight' | 'idle';

const num = (v: string | null | undefined): number => {
  const n = parseFloat(v ?? '0');
  return Number.isFinite(n) ? n : 0;
};

export function payrollRowState(
  row: Pick<PayrollLineItem, 'totalDue' | 'inFlight'> & { lint: Pick<PayrollLineItem['lint'], 'providerKey' | 'missingFields'> },
): PayrollRowState {
  const due = num(row.totalDue);
  if (num(row.inFlight) > 0) return 'in_flight';
  if (due <= 0) return 'idle';
  if (row.lint.missingFields.length > 0 || row.lint.providerKey === 'unknown') return 'blocked';
  return 'ready';
}

/** Why a row is blocked, in words, or null when it isn't. */
export function blockedReason(
  row: { lint: Pick<PayrollLineItem['lint'], 'providerKey' | 'missingFields'> },
): string | null {
  if (row.lint.providerKey === 'unknown') return "Couldn't work out how to pay this outreacher. Check their payout provider.";
  if (row.lint.missingFields.length > 0) return 'Missing payment details.';
  return null;
}

export interface OutstandingSummary {
  /** Money still owed: the sum of positive balances only. */
  owed: number;
  /** Paid ahead: the sum of negative balances, as a positive number. */
  credit: number;
  /** Outreachers with a negative balance. */
  creditCount: number;
}

/**
 * What's still owed across payroll rows. A negative balance (paid ahead, or
 * an adjustment larger than what was earned) is credit for that one
 * outreacher, not a discount on everyone else, so it never nets against
 * what others are owed.
 */
export function outstandingSummary(rows: ReadonlyArray<{ totalDue: string }>): OutstandingSummary {
  let owedCents = 0;
  let creditCents = 0;
  let creditCount = 0;
  for (const r of rows) {
    const cents = Math.round((parseFloat(r.totalDue) || 0) * 100);
    if (cents > 0) owedCents += cents;
    else if (cents < 0) {
      creditCents -= cents;
      creditCount += 1;
    }
  }
  return { owed: owedCents / 100, credit: creditCents / 100, creditCount };
}
